// src/components/editor/DynamicContentManager.jsx
import React, { useState } from 'react';
import { 
  Calendar, Tag, Clock, Plus, Edit3, Trash2, Copy, 
  AlertCircle, CheckCircle, Settings, Play, Pause
} from 'lucide-react';

const DynamicContentManager = ({ template, onUpdateTemplate, variables = [] }) => {
  const [activeTab, setActiveTab] = useState('variables');
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [previewTime, setPreviewTime] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    dayOfWeek: new Date().getDay()
  });

  // Règles temporelles existantes
  const [temporalRules, setTemporalRules] = useState([
    {
      id: 'rule-1',
      name: 'Prix Week-end',
      description: 'Prix majorés le weekend',
      priority: 1,
      isActive: true,
      conditions: {
        daysOfWeek: [0, 6],
        timeRange: null,
        dateRange: null
      },
      values: {
        'price_petit_dej': '3€20',
        'price_grand_dej': '7€50',
        'special_offer': 'Prix weekend en vigueur'
      },
      createdAt: '2025-06-20T10:00:00Z'
    }
  ]);

  // Obtenir les éléments dynamiques du template
  const getDynamicElements = () => {
    return template?.elements?.filter(el => el.isDynamic && el.dynamicTag) || [];
  };

  // Simuler l'application des règles à un moment donné
  const getActiveRulesForTime = (date, time, dayOfWeek) => {
    return temporalRules.filter(rule => {
      if (!rule.isActive) return false;

      if (rule.conditions.daysOfWeek && !rule.conditions.daysOfWeek.includes(dayOfWeek)) {
        return false;
      }

      if (rule.conditions.timeRange) {
        const currentTime = time;
        const { start, end } = rule.conditions.timeRange;
        if (currentTime < start || currentTime > end) {
          return false;
        }
      }

      if (rule.conditions.dateRange) {
        const currentDate = date;
        const { start, end } = rule.conditions.dateRange;
        if (currentDate < start || currentDate > end) {
          return false;
        }
      }

      return true;
    }).sort((a, b) => b.priority - a.priority);
  };

  // Obtenir les valeurs à afficher pour un moment donné
  const getValuesForTime = (date, time, dayOfWeek) => {
    const activeRules = getActiveRulesForTime(date, time, dayOfWeek);
    const values = {};

    // Commencer par les valeurs par défaut des variables
    variables.forEach(variable => {
      values[variable.id] = variable.defaultValue;
    });

    // Appliquer les règles dans l'ordre de priorité
    activeRules.forEach(rule => {
      Object.entries(rule.values).forEach(([key, value]) => {
        values[key] = value;
      });
    });

    return values;
  };

  // Modal de confirmation personnalisée
  const ConfirmationModal = ({ ruleId, ruleName, onConfirm, onCancel }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-md w-full mx-4 overflow-hidden">
        <div className="p-6">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <Trash2 className="h-5 w-5 text-red-600" />
            </div>
            
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-800 mb-2">
                Supprimer la règle
              </h3>
              <p className="text-gray-600 text-sm">
                Êtes-vous sûr de vouloir supprimer la règle "{ruleName}" ? Cette action est irréversible.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={() => {
              onConfirm(ruleId);
              onCancel();
            }}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            Supprimer
          </button>
        </div>
      </div>
    </div>
  );

  // Formulaire de règle temporelle
  const RuleForm = ({ rule, onSave, onCancel }) => {
    const [formData, setFormData] = useState(rule || {
      name: '',
      description: '',
      priority: 1,
      isActive: true,
      conditions: {
        daysOfWeek: [],
        timeRange: null,
        dateRange: null
      },
      values: {}
    });

    const updateCondition = (type, value) => {
      setFormData(prev => ({
        ...prev,
        conditions: {
          ...prev.conditions,
          [type]: value
        }
      }));
    };

    const updateValue = (variableId, value) => {
      setFormData(prev => ({
        ...prev,
        values: {
          ...prev.values,
          [variableId]: value
        }
      }));
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      const ruleToSave = {
        ...formData,
        id: rule?.id || `rule-${Date.now()}`,
        createdAt: rule?.createdAt || new Date().toISOString()
      };
      onSave(ruleToSave);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b">
            <h2 className="text-xl font-bold">
              {rule ? 'Modifier' : 'Nouvelle'} Règle Temporelle
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Informations générales */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Nom de la règle</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Priorité</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value={1}>1 - Faible</option>
                  <option value={2}>2 - Normale</option>
                  <option value={3}>3 - Élevée</option>
                  <option value={4}>4 - Très élevée</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg"
                rows={2}
              />
            </div>

            {/* Valeurs à appliquer */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Valeurs à Appliquer</h3>
              <div className="space-y-3">
                {variables.map(variable => (
                  <div key={variable.id} className="flex items-center space-x-3">
                    <div className="w-1/3">
                      <span className="text-sm font-medium">{variable.name}</span>
                    </div>
                    <div className="flex-1">
                      <input
                        type="text"
                        value={formData.values[variable.id] || ''}
                        onChange={(e) => updateValue(variable.id, e.target.value)}
                        placeholder={`Défaut: ${variable.defaultValue}`}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {rule ? 'Modifier' : 'Créer'} la Règle
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col">
      {/* En-tête */}
      <div className="p-4 border-b">
        <h2 className="text-lg font-semibold">Contenu Dynamique</h2>
        <p className="text-sm text-gray-600">
          Gérez les éléments variables et leurs règles temporelles
        </p>
      </div>

      {/* Onglets */}
      <div className="flex border-b">
        <button
          onClick={() => setActiveTab('variables')}
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === 'variables'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Variables ({getDynamicElements().length})
        </button>
        <button
          onClick={() => setActiveTab('rules')}
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === 'rules'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Règles ({temporalRules.length})
        </button>
        <button
          onClick={() => setActiveTab('preview')}
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === 'preview'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Prévisualisation
        </button>
      </div>

      {/* Contenu des onglets */}
      <div className="flex-1 overflow-y-auto">
        
        {/* Onglet Variables */}
        {activeTab === 'variables' && (
          <div className="p-4 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-medium">Éléments Variables du Template</h3>
              <span className="text-sm text-gray-500">
                {getDynamicElements().length} éléments configurés
              </span>
            </div>

            {getDynamicElements().length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Tag className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>Aucun élément variable configuré</p>
                <p className="text-sm mt-1">
                  Activez le mode "Variable temporelle" sur vos éléments texte
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {getDynamicElements().map(element => {
                  const variable = variables.find(v => v.id === element.dynamicTag);
                  return (
                    <div key={element.id} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <Tag className="h-4 w-4 text-blue-600" />
                            <span className="font-medium text-sm">
                              {variable?.name || element.dynamicTag}
                            </span>
                          </div>
                          <div className="text-xs text-gray-600 mb-2">
                            Position: ({Math.round(element.x)}, {Math.round(element.y)}) • 
                            Taille: {element.fontSize}px
                          </div>
                          <div className="text-sm">
                            <span className="text-gray-600">Contenu actuel: </span>
                            <span className="font-medium">{element.content}</span>
                          </div>
                          <div className="text-sm mt-1">
                            <span className="text-gray-600">Valeur par défaut: </span>
                            <span className="text-gray-800">{variable?.defaultValue}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1">
                          <span className={`px-2 py-1 rounded text-xs ${
                            variable ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {variable ? 'Configuré' : 'Variable inconnue'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Liste des variables disponibles */}
            <div className="mt-8">
              <h3 className="font-medium mb-3">Variables Disponibles</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {variables.map(variable => (
                  <div key={variable.id} className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">{variable.name}</span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        variable.category === 'prix' ? 'bg-green-100 text-green-700' :
                        variable.category === 'info' ? 'bg-blue-100 text-blue-700' :
                        variable.category === 'promotion' ? 'bg-orange-100 text-orange-700' :
                        'bg-purple-100 text-purple-700'
                      }`}>
                        {variable.category}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mb-2">{variable.description}</p>
                    <div className="text-sm">
                      <span className="text-gray-500">ID: </span>
                      <code className="bg-gray-100 px-1 rounded text-xs">{variable.id}</code>
                    </div>
                    <div className="text-sm mt-1">
                      <span className="text-gray-500">Exemple: </span>
                      <span className="font-medium">{variable.defaultValue}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Onglet Règles */}
        {activeTab === 'rules' && (
          <div className="p-4 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-medium">Règles Temporelles</h3>
              <button
                onClick={() => setShowRuleForm(true)}
                className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus className="h-4 w-4" />
                <span>Nouvelle Règle</span>
              </button>
            </div>

            <div className="space-y-3">
              {temporalRules.map(rule => (
                <div key={rule.id} className="p-4 border rounded-lg">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-medium">{rule.name}</span>
                        <span className={`px-2 py-1 rounded text-xs ${
                          rule.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {rule.isActive ? 'Actif' : 'Inactif'}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs ${
                          rule.priority <= 2 ? 'bg-blue-100 text-blue-700' :
                          rule.priority <= 3 ? 'bg-orange-100 text-orange-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          Priorité {rule.priority}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{rule.description}</p>
                      
                      {/* Valeurs modifiées */}
                      <div className="mt-2">
                        <span className="text-gray-500 text-sm">Modifie: </span>
                        <span className="text-sm">
                          {Object.keys(rule.values).map(key => 
                            variables.find(v => v.id === key)?.name || key
                          ).join(', ')}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => {
                          setSelectedRule(rule);
                          setShowRuleForm(true);
                        }}
                        className="p-2 hover:bg-gray-100 rounded"
                        title="Modifier"
                      >
                        <Edit3 className="h-4 w-4 text-gray-600" />
                      </button>
                      <button
                        onClick={() => {
                          setTemporalRules(prev => prev.map(r => 
                            r.id === rule.id ? { ...r, isActive: !r.isActive } : r
                          ));
                        }}
                        className={`p-2 hover:bg-gray-100 rounded ${
                          rule.isActive ? 'text-orange-600' : 'text-green-600'
                        }`}
                        title={rule.isActive ? 'Désactiver' : 'Activer'}
                      >
                        {rule.isActive ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => {
                          const duplicated = {
                            ...rule,
                            id: `rule-${Date.now()}`,
                            name: `${rule.name} (Copie)`,
                            createdAt: new Date().toISOString()
                          };
                          setTemporalRules(prev => [...prev, duplicated]);
                        }}
                        className="p-2 hover:bg-gray-100 rounded"
                        title="Dupliquer"
                      >
                        <Copy className="h-4 w-4 text-gray-600" />
                      </button>
                      <button
                        onClick={() => {
                          setShowDeleteConfirm(rule.id);
                        }}
                        className="p-2 hover:bg-red-100 rounded"
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {temporalRules.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>Aucune règle temporelle configurée</p>
                  <p className="text-sm mt-1">
                    Créez des règles pour automatiser les changements de contenu
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Onglet Prévisualisation */}
        {activeTab === 'preview' && (
          <div className="p-4 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-medium">Simulation Temporelle</h3>
            </div>

            {/* Contrôles de simulation */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium mb-1">Date</label>
                <input
                  type="date"
                  value={previewTime.date}
                  onChange={(e) => {
                    const newDate = new Date(e.target.value);
                    setPreviewTime(prev => ({
                      ...prev,
                      date: e.target.value,
                      dayOfWeek: newDate.getDay()
                    }));
                  }}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Heure</label>
                <input
                  type="time"
                  value={previewTime.time}
                  onChange={(e) => setPreviewTime(prev => ({ ...prev, time: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Jour</label>
                <select
                  value={previewTime.dayOfWeek}
                  onChange={(e) => setPreviewTime(prev => ({ ...prev, dayOfWeek: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  {['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'].map((day, index) => (
                    <option key={index} value={index}>{day}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Résultats de la simulation */}
            {(() => {
              const activeRules = getActiveRulesForTime(
                previewTime.date, 
                previewTime.time, 
                previewTime.dayOfWeek
              );
              const currentValues = getValuesForTime(
                previewTime.date, 
                previewTime.time, 
                previewTime.dayOfWeek
              );

              return (
                <div className="space-y-4">
                  {/* Valeurs résultantes */}
                  <div>
                    <h4 className="font-medium mb-2">Valeurs Affichées</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {Object.entries(currentValues).map(([key, value]) => {
                        const variable = variables.find(v => v.id === key);
                        const isModified = variable && value !== variable.defaultValue;
                        
                        return (
                          <div key={key} className={`p-3 rounded-lg border ${
                            isModified 
                              ? 'bg-green-50 border-green-200' 
                              : 'bg-gray-50 border-gray-200'
                          }`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm">
                                {variable?.name || key}
                              </span>
                              {isModified && (
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              )}
                            </div>
                            <div className={`text-sm ${
                              isModified ? 'text-green-800' : 'text-gray-700'
                            }`}>
                              {value}
                            </div>
                            {isModified && (
                              <div className="text-xs text-green-600 mt-1">
                                (Défaut: {variable?.defaultValue})
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Modal de confirmation de suppression */}
      {showDeleteConfirm && (
        <ConfirmationModal
          ruleId={showDeleteConfirm}
          ruleName={temporalRules.find(r => r.id === showDeleteConfirm)?.name || 'Règle'}
          onConfirm={(ruleId) => {
            setTemporalRules(prev => prev.filter(r => r.id !== ruleId));
          }}
          onCancel={() => setShowDeleteConfirm(null)}
        />
      )}

      {/* Formulaire de règle */}
      {showRuleForm && (
        <RuleForm
          rule={selectedRule}
          onSave={(rule) => {
            if (selectedRule) {
              setTemporalRules(prev => prev.map(r => r.id === rule.id ? rule : r));
            } else {
              setTemporalRules(prev => [...prev, rule]);
            }
            setShowRuleForm(false);
            setSelectedRule(null);
          }}
          onCancel={() => {
            setShowRuleForm(false);
            setSelectedRule(null);
          }}
        />
      )}
    </div>
  );
};

export default DynamicContentManager;